import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';

const MetricCardContainer = styled(Box)(({ theme }) => [
  {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.customShadows.sm,
    padding: theme.spacing(3),
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  theme.applyStyles('dark', {
    border: 0,
    boxShadow: 'none',
  }),
]);

interface MetricProps {
  title: string;
  description: string;
  percentageValue: string | undefined;
  percentageValueFallback: string;
  numericDescription: string | undefined;
  numericDescriptionFallback: string;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  Icon: React.ComponentType<SvgIconProps>;
}

const MetricCard = ({
  title,
  description,
  percentageValue,
  percentageValueFallback,
  numericDescription,
  numericDescriptionFallback,
  isFetching,
  isError,
  Icon,
}: MetricProps) => {
  return (
    <MetricCardContainer>
      <Typography variant='body1'>{title}</Typography>
      <Typography variant='body3' pt={0.5} color='textSecondary'>
        {description}
      </Typography>

      <Stack
        direction='row'
        alignItems='flex-end'
        justifyContent='space-between'
        gap={1}
        pt={2.25}
      >
        <Box>
          {isFetching ? (
            <>
              <Skeleton>
                <Typography variant='h3' component='p'>
                  {percentageValueFallback}
                </Typography>
              </Skeleton>
              <Skeleton>
                <Typography variant='body2' pt={1}>
                  {numericDescriptionFallback}
                </Typography>
              </Skeleton>
            </>
          ) : isError ? (
            <>
              <Typography variant='h3' component='p'>
                {percentageValueFallback}
              </Typography>
              <Typography variant='body2' pt={1}>
                {numericDescriptionFallback}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant='h3' component='p'>
                {percentageValue}
              </Typography>
              <Typography variant='body2' pt={1}>
                {numericDescription}
              </Typography>
            </>
          )}
        </Box>

        <Icon color='primary' sx={{ width: 60, height: 60 }} />
      </Stack>
    </MetricCardContainer>
  );
};

export default MetricCard;
